import { useQuery, useQueryClient } from "@tanstack/react-query";
import { signOutTarget } from "../lib/signOutTarget";
import { onIdTokenChanged, signOut as firebaseSignOut, type Auth, type User } from "firebase/auth";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";
import { ApiError, configureApi, get } from "../lib/api";
import { activatePrivateSession, endPrivateSession, setPrivateHousehold } from "../lib/privateCache";
import type { HouseholdSummary, Me, PublicConfig, Role } from "../lib/types";
import { rememberNext, withNext } from "../routes/guards";

type SignOutReason = "user" | "expired" | "deleted";

interface AuthContextValue {
  config: PublicConfig;
  auth: Auth;
  user: User | null;
  /** Firebase has not yet reported the persisted session. */
  initializing: boolean;
  me: Me | undefined;
  meLoading: boolean;
  meError: unknown;
  refreshMe: () => Promise<Me | undefined>;
  /** Reload the Firebase user and force a fresh ID token (after email verification). */
  refreshVerification: () => Promise<boolean>;
  signOut: (reason?: SignOutReason) => Promise<void>;
  householdId: string | null;
  household: HouseholdSummary | null;
  selectHousehold: (id: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const householdKey = (uid: string) => `sk:household:${uid}`;
function readStoredHousehold(uid: string): string | null {
  try {
    return localStorage.getItem(householdKey(uid));
  } catch {
    return null;
  }
}
function storeHousehold(uid: string, id: string | null) {
  try {
    if (id) localStorage.setItem(householdKey(uid), id);
    else localStorage.removeItem(householdKey(uid));
  } catch {
    // ignore
  }
}

export function AuthProvider({ config, auth, children }: { config: PublicConfig; auth: Auth; children: ReactNode }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [initializing, setInitializing] = useState(true);
  // Firebase mutates the same User object on reload(); bump to re-render consumers.
  const [userVersion, setUserVersion] = useState(0);
  const [storedHousehold, setStoredHousehold] = useState<string | null>(null);
  const lastUid = useRef<string | null>(null);
  const sessionReported = useRef(false);
  const locationRef = useRef(location);
  locationRef.current = location;
  const signingOut = useRef(false);

  useEffect(() => {
    return onIdTokenChanged(auth, (next) => {
      const nextUid = next?.uid ?? null;
      // First report (persisted session at startup) or an account change, including sign-in/out in
      // another tab: bind the private cache to this user. That synchronously invalidates in-flight cache
      // work and deletes every other account's caches (all of them when signed out) while keeping this
      // user's own, so a signed-in device can still boot offline.
      if (!sessionReported.current || lastUid.current !== nextUid) {
        const changed = sessionReported.current;
        sessionReported.current = true;
        void activatePrivateSession(nextUid);
        if (changed) {
          // A different (or no) user: nothing private from the previous session may remain.
          void queryClient.cancelQueries();
          queryClient.clear();
        }
        lastUid.current = nextUid;
        setStoredHousehold(nextUid ? readStoredHousehold(nextUid) : null);
      }
      setUser(next);
      setInitializing(false);
    });
  }, [auth, queryClient]);

  const signOut = useCallback(
    async (reason: SignOutReason = "user") => {
      if (signingOut.current) return;
      signingOut.current = true;
      try {
        const here = locationRef.current;
        const next = `${here.pathname}${here.search}`;
        // Invalidate synchronously before any await, then cancel and drop queries, then wait for the
        // (serialized) cache deletion.
        const cleared = endPrivateSession();
        await queryClient.cancelQueries();
        queryClient.clear();
        await cleared;
        const params = new URLSearchParams();
        if (reason === "expired") {
          params.set("reason", "expired");
          if (!here.pathname.startsWith("/login")) params.set("next", next);
        } else if (reason === "deleted") params.set("reason", "deleted");
        const target = `/login${params.size ? `?${params}` : ""}`;
        signOutTarget.current = target;
        await firebaseSignOut(auth);
        navigate(target, { replace: true });
      } finally {
        signingOut.current = false;
        setTimeout(() => {
          signOutTarget.current = null;
        }, 0);
      }
    },
    [auth, navigate, queryClient],
  );

  const meQuery = useQuery({
    queryKey: ["me", user?.uid],
    queryFn: ({ signal }) => get<Me>("/api/v1/me", signal),
    enabled: !!user,
    staleTime: 30_000,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
  });

  // Wire the API client to the current Firebase session.
  useEffect(() => {
    configureApi({
      getToken: async (force) => (auth.currentUser ? auth.currentUser.getIdToken(force) : null),
      getUid: () => auth.currentUser?.uid ?? null,
      onSessionExpired: () => void signOut("expired"),
      onEmailNotVerified: () => {
        const here = locationRef.current;
        if (!here.pathname.startsWith("/verify-email")) {
          const next = `${here.pathname}${here.search}`;
          rememberNext(next);
          navigate(withNext("/verify-email", next), { replace: true });
        }
      },
    });
  }, [auth, navigate, signOut]);

  const refetchMe = meQuery.refetch;
  const refreshMe = useCallback(async () => {
    const result = await refetchMe();
    return result.data;
  }, [refetchMe]);

  const refreshVerification = useCallback(async () => {
    const current = auth.currentUser;
    if (!current) return false;
    await current.reload();
    await current.getIdToken(true);
    setUser(auth.currentUser);
    setUserVersion((v) => v + 1);
    const fresh = await refetchMe();
    return !!auth.currentUser?.emailVerified && !!fresh.data?.user.emailVerified;
  }, [auth, refetchMe]);

  const me = meQuery.data;
  const households = me?.households ?? [];
  const household = households.find((h) => h.id === storedHousehold) ?? households[0] ?? null;
  const householdId = household?.id ?? null;

  // Bind cache writes to the active household once it is known (also when it changes because the
  // stored household disappeared from /me).
  useEffect(() => {
    if (user && householdId) void setPrivateHousehold(user.uid, householdId);
  }, [user, householdId]);

  const selectHousehold = useCallback(
    async (id: string) => {
      if (!user) return;
      if (id !== householdId) {
        // Switching households: wipe every private cache and all query data first.
        const cleared = setPrivateHousehold(user.uid, id, true);
        const notMe = { predicate: (q: { queryKey: readonly unknown[] }) => q.queryKey[0] !== "me" };
        await queryClient.cancelQueries(notMe);
        queryClient.removeQueries(notMe);
        await cleared;
      }
      storeHousehold(user.uid, id);
      setStoredHousehold(id);
    },
    [householdId, queryClient, user],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      config,
      auth,
      user,
      initializing,
      me,
      meLoading: meQuery.isPending && !!user,
      meError: meQuery.error,
      refreshMe,
      refreshVerification,
      signOut,
      householdId,
      household,
      selectHousehold,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, auth, user, userVersion, initializing, me, meQuery.isPending, meQuery.error, refreshMe, refreshVerification, signOut, householdId, household, selectHousehold],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}

/** The active household (only valid inside the household-gated app shell). */
export function useHousehold(): { householdId: string; household: HouseholdSummary; role: Role; canManage: boolean; isOwner: boolean } {
  const { householdId, household } = useAuth();
  if (!householdId || !household) throw new Error("useHousehold outside household shell");
  return {
    householdId,
    household,
    role: household.role,
    canManage: household.role === "owner" || household.role === "admin",
    isOwner: household.role === "owner",
  };
}
